import { apiGet, apiPost, apiDelete, planningPath } from './PlanningApiClient';

const LS_KEY = 'anojeje_vendor_categories';

const DEFAULT_VENDOR_CATEGORIES: readonly string[] = [
  'Venue',
  'Catering',
  'Photography',
  'Videography',
  'Music',
  'Flowers',
  'Transportation',
  'Decor',
  'Makeup & Hair',
  'Event Planning',
  'MC & Entertainment',
  'Cake',
  'Stationery',
  'Fashion & Styling',
] as const;

// ── In-memory cache (source of truth after first API fetch) ──────────────────
let categoriesCache: string[] | null = null;

function lsRead(): string[] | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const filtered = Array.isArray(parsed) ? parsed.filter((v: unknown) => typeof v === 'string' && v.trim()) : [];
    return filtered.length > 0 ? filtered : null;
  } catch { return null; }
}

function lsWrite(categories: string[]): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(categories)); } catch { /* quota */ }
}

export function loadVendorCategories(): string[] {
  return categoriesCache ?? lsRead() ?? [...DEFAULT_VENDOR_CATEGORIES];
}

export async function refreshVendorCategoriesFromApi(): Promise<string[]> {
  try {
    const res = await apiGet<{ categories: (string | { id: string; name: string })[] }>(planningPath('/vendor-categories'));
    const items = res.categories ?? [];
    const names = items.map(c => (typeof c === 'string' ? c : c.name)).filter(Boolean) as string[];
    if (names.length > 0) {
      categoriesCache = names;
      lsWrite(names);
      return names;
    }
    return loadVendorCategories();
  } catch {
    return loadVendorCategories();
  }
}

export async function saveVendorCategories(categories: string[]): Promise<void> {
  // Optimistic local update
  categoriesCache = categories;
  lsWrite(categories);

  // Sync to API: fetch current DB list, add any new names, remove absent ones
  let failedWrites = 0;
  try {
    const res = await apiGet<{ categories: { id: string; name: string }[] }>(planningPath('/vendor-categories'));
    const existing = res.categories ?? [];
    const existingNames = existing.map(c => c.name);
    const toAdd = categories.filter(n => !existingNames.includes(n));
    const toRemove = existing.filter(c => !categories.includes(c.name));
    // allSettled, not all: one rejected add/remove must not abort the others.
    const results = await Promise.allSettled([
      ...toAdd.map(name => apiPost(planningPath('/vendor-categories'), { name })),
      ...toRemove.map(c => apiDelete(planningPath(`/vendor-categories/${c.id}`))),
    ]);
    failedWrites = results.filter(r => r.status === 'rejected').length;
  } catch { /* API unreachable — local cache is up to date */ }

  // Thrown outside the try so the catch above can't swallow it. The local list
  // is already written, so this is a sync failure (other devices won't see the
  // change), not data loss on this one.
  if (failedWrites > 0) {
    throw new Error(
      `${failedWrites} vendor category change${failedWrites === 1 ? '' : 's'} could not be saved to the server. Your list is up to date on this device only.`,
    );
  }
}

export interface VendorCategoryRecord { id: string; name: string }

function remember(records: VendorCategoryRecord[]): void {
  const names = records.map(r => r.name);
  categoriesCache = names;
  lsWrite(names);
}

/** The workspace's categories with their ids. Unlike refreshVendorCategoriesFromApi it throws, so a screen can show the failure. */
export async function listVendorCategories(): Promise<VendorCategoryRecord[]> {
  const res = await apiGet<{ categories?: (string | { id: string | number; name: string })[] }>(planningPath('/vendor-categories'));
  const records = (res.categories ?? [])
    .filter((c): c is { id: string | number; name: string } => typeof c !== 'string' && !!c?.name)
    .map(c => ({ id: String(c.id), name: c.name }));
  remember(records);
  return records;
}

/** Adds one category. Touches nothing else, so it cannot archive a category another person just added. */
export async function addVendorCategory(name: string): Promise<void> {
  await apiPost(planningPath('/vendor-categories'), { name: name.trim() });
}

export async function removeVendorCategory(id: string): Promise<void> {
  await apiDelete(planningPath(`/vendor-categories/${id}`));
}

export { LS_KEY as VENDOR_CATEGORIES_KEY, DEFAULT_VENDOR_CATEGORIES };
